// src/pages/packingBatch/PackingBatchListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Eye,
  RefreshCw,
  Download,
  Truck,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Printer
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import {
  getAllPackingBatches,
  getUserWarehouses,
  retryFailedOrders
} from '../../services/api/packingBatch';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import styles from './PackingBatchListPage.module.css';

const PackingBatchListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // State management
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState({});

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    warehouseId: '',
    businessCourierId: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Reference data for filters
  const [referenceData, setReferenceData] = useState({
    warehouses: [],
    courierAccounts: []
  });

  // Bulk actions
  const [selectedBatches, setSelectedBatches] = useState([]);

  // Status configuration
  const statusConfig = {
    in_progress: {
      label: t('packingBatch.statusInProgress'),
      color: '#3b82f6',
      bgColor: '#eff6ff',
      icon: <Clock size={14} />
    },
    completed: {
      label: t('packingBatch.statusCompleted'),
      color: '#10b981',
      bgColor: '#d1fae5',
      icon: <CheckCircle size={14} />
    },
    partially_failed: {
      label: t('packingBatch.statusPartiallyFailed'),
      color: '#f59e0b',
      bgColor: '#fef3c7',
      icon: <AlertTriangle size={14} />
    },
    failed: {
      label: t('packingBatch.statusFailed'),
      color: '#ef4444',
      bgColor: '#fee2e2',
      icon: <XCircle size={14} />
    }
  };

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Load batches when filters or pagination/sorting changes
  useEffect(() => {
    fetchBatches();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const loadReferenceData = async () => {
    try {
      const [warehousesData, couriersData] = await Promise.all([
        getUserWarehouses(),
        getBusinessCouriers()
      ]);
      
      setReferenceData({
        warehouses: warehousesData.warehouses || [],
        courierAccounts: couriersData.couriers || []
      });
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const fetchBatches = async () => {
    try {
      setIsLoading(true);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
        channel: 'online'
      };

      // Add filters
      if (filters.search) params.search = filters.search;
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.businessCourierId) params.businessCourierId = filters.businessCourierId;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await getAllPackingBatches(params);
      setBatches(response.batches || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));

    } catch (err) {
      console.error('Failed to fetch batches:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('packingBatch.failedToLoadBatches'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      warehouseId: '',
      businessCourierId: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  const handleRetryFailedOrders = async (batchId, batchNumber) => {
    if (!window.confirm(t('packingBatch.confirmRetryFailed', { batchNumber }))) {
      return;
    }

    setIsRetrying(prev => ({ ...prev, [batchId]: true }));
    try {
      const result = await retryFailedOrders(batchId, []);
      
      if (result.retriedOrders.length > 0) {
        success(t('packingBatch.retrySuccessCount', { count: result.retriedOrders.length }));
        fetchBatches();
      } else {
        showError(t('packingBatch.noOrdersRetried'));
      }
    } catch (err) {
      console.error('Retry failed:', err);
      showError(err.response?.data?.message || t('packingBatch.retryError'));
    } finally {
      setIsRetrying(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const handleBulkRetry = async () => {
    const partiallyFailedBatches = selectedBatches.filter(id => {
      const batch = batches.find(b => b.id === id);
      return batch && batch.status === 'partially_failed';
    });

    if (partiallyFailedBatches.length === 0) {
      showError(t('packingBatch.noFailedBatchesSelected'));
      return;
    }

    if (!window.confirm(t('packingBatch.confirmBulkRetry', { count: partiallyFailedBatches.length }))) {
      return;
    }

    try {
      await Promise.all(partiallyFailedBatches.map(id => retryFailedOrders(id, [])));
      success(t('packingBatch.bulkRetrySuccessful', { count: partiallyFailedBatches.length }));
      setSelectedBatches([]);
      fetchBatches();
    } catch (err) {
      console.error('Bulk retry failed:', err);
      showError(t('packingBatch.failedToRetrySome'));
    }
  };

  const handleExport = () => {
    const headers = [
      t('packingBatch.batchNumber'),
      t('packingBatch.courierAccount'),
      t('packingBatch.warehouse'),
      t('packingBatch.totalOrders'),
      t('packingBatch.totalValue'),
      t('packingBatch.status'),
      t('packingBatch.created'),
      t('packingBatch.createdBy')
    ];
    
    const rows = batches.map(batch => [
      batch.batchNumber,
      batch.accountInfo?.name || batch.courierAccount?.accountName || t('common.na'),
      batch.Warehouse?.name || t('common.na'),
      batch.totalOrders,
      batch.totalValue,
      statusConfig[batch.status]?.label || batch.status,
      formatDate(batch.createdAt),
      batch.createdBy?.name || t('common.na')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packing-batches-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('packingBatch.warehouse'),
      type: 'select',
      icon: <Truck size={18} />,
      placeholder: t('packingBatch.selectWarehouse'),
      options: referenceData.warehouses.map(w => ({
        value: w.id.toString(),
        label: w.name
      }))
    },
    {
      key: 'businessCourierId',
      label: t('packingBatch.courierAccount'),
      type: 'select',
      placeholder: t('packingBatch.allCourierAccounts'),
      options: referenceData.courierAccounts.map(a => ({
        value: a.id.toString(),
        label: `${a.accountName} (${a.CourierCompany?.name})`
      }))
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: t('packingBatch.allStatuses'),
      options: Object.entries(statusConfig).map(([key, config]) => ({
        value: key,
        label: config.label
      }))
    },
    {
      key: 'date',
      label: t('packingBatch.dateRange'),
      type: 'daterange',
      gridColumn: 'span 2'
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'batchNumber',
      header: t('packingBatch.batchNumber'),
      sortable: true,
      width: '150px',
      render: (row) => (
        <div className={styles.batchNumber}>{row.batchNumber}</div>
      )
    },
    {
      key: 'createdAt',
      header: t('packingBatch.created'),
      sortable: true,
      width: '160px',
      render: (row) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}>
            <Calendar size={12} />
            {formatDate(row.createdAt)}
          </div>
          <div className={styles.timeInfo}>
            {formatTime(row.createdAt)}
          </div>
        </div>
      )
    },
    {
      key: 'warehouse',
      header: t('packingBatch.warehouse'),
      width: '150px',
      render: (row) => (
        <div className={styles.warehouseBadge}>
          {row.Warehouse?.name || t('common.na')}
        </div>
      )
    },
    {
      key: 'courierAccount',
      header: t('packingBatch.courierAccount'),
      width: '180px',
      render: (row) => (
        <div className={styles.courierCell}>
          <div className={styles.courierBadge}>
            <Truck size={12} />
            {row.accountInfo?.name || row.courierAccount?.accountName || t('common.na')}
          </div>
          {row.CourierCompany?.name && (
            <div className={styles.courierCompanyName}>
              {row.CourierCompany.name}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'totalOrders',
      header: t('packingBatch.totalOrders'),
      sortable: true,
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className={styles.ordersCount}>
          <Package size={12} />
          {row.totalOrders}
        </div>
      )
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '140px',
      render: (row) => {
        const statusInfo = statusConfig[row.status] || statusConfig.in_progress;
        return (
          <div 
            className={styles.statusBadge}
            style={{
              backgroundColor: statusInfo.bgColor,
              borderColor: statusInfo.color,
              color: statusInfo.color
            }}
          >
            {statusInfo.icon}
            <span>{statusInfo.label}</span>
          </div>
        );
      }
    },
    {
      key: 'isPrinted',
      header: t('packingBatch.printed'),
      width: '110px',
      align: 'center',
      render: (row) => (
        <div className={`${styles.printStatusBadge} ${row.isPrinted ? styles.printStatusPrinted : styles.printStatusNotPrinted}`}>
          <Printer size={13} />
          <span>{row.isPrinted ? t('packingBatch.printed') : t('packingBatch.notPrinted')}</span>
        </div>
      )
    },
    {
      key: 'createdBy',
      header: t('common.createdBy'),
      width: '150px',
      render: (row) => (
        <div className={styles.createdByInfo}>
          {row.createdBy?.name || t('common.na')}
        </div>
      )
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        <Link
          to={`/packing-batches/${row.id}`}
          className={`${styles.actionButton} ${styles.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={16} />
        </Link>

        {row.status === 'partially_failed' && hasPermission('create_packing_batch') && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRetryFailedOrders(row.id, row.batchNumber);
            }}
            disabled={isRetrying[row.id]}
            className={`${styles.actionButton} ${styles.retryButton}`}
            title={t('packingBatch.retryFailedOrders')}
          >
            {isRetrying[row.id] ? (
              <div className={styles.smallSpinner}></div>
            ) : (
              <RefreshCw size={16} />
            )}
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            // Export single batch logic here if needed
          }}
          className={`${styles.actionButton} ${styles.exportButton}`}
          title={t('common.export')}
        >
          <Download size={16} />
        </button>
      </div>
    ),
    width: '140px',
    frozen: true
  };

  return (
    <div className={styles.packingBatchListPage}>
      {/* Page Header */}
      <PageHeader
        title={t('packingBatch.allPackingBatches')}
        description={t('packingBatch.pageDescription')}
        icon={Package}
        actions={
          hasPermission('create_packing_batch') && (
            <Link to="/packing-batches/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('packingBatch.createPackingBatch')}
            </Link>
          )
        }
      />

      {/* Bulk Actions Bar (shown when items selected) */}
      {selectedBatches.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <span className={styles.bulkActionsText}>
            {t('common.selectedCount', { count: selectedBatches.length })}
          </span>
          <button
            onClick={handleBulkRetry}
            className={styles.bulkActionButton}
          >
            <RefreshCw size={18} />
            {t('packingBatch.retrySelected', { count: selectedBatches.length })}
          </button>
        </div>
      )}

      {/* Search and Filters Row */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('packingBatch.searchByBatchNumber')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={batches}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedBatches}
        onSelectionChange={setSelectedBatches}
        onRowClick={(row) => navigate(`/packing-batches/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() 
            ? t('packingBatch.noBatchesFound')
            : t('packingBatch.noBatchesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('packingBatch.noBatchesFoundDescription')
            : t('packingBatch.createFirstBatch')
        }
        emptyIcon={<Package size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={() => {
              handleClearFilters();
            }} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_packing_batch') ? (
            <Link to="/packing-batches/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('packingBatch.createPackingBatch')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default PackingBatchListPage;